import React, { useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import CART_ITEM from '../../constants/cartItems';
import { CURRENCY } from '../../../../constants/currency';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import CartItemPhotoPreview from './CartItemPhotoPreview';
import { getAsset } from '../../../../util/assets';
import CheckoutItemQuantityInput from './CheckoutItemQuantityInput';
import useAlbumPrintPackageCartConfigurator from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumPrintPackageOption/useAlbumPrintPackageCartConfigurator';
import PrintPackageConfigurableItemCompleteModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumPrintPackageOption/components/PrintPackageConfigurableItemCompleteModal';
import AccountCardAlbumCustomPrintConfiguratorModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import { PrintProductConfiguratorModal } from '../../../prints/components/PrintProductConfigurator';
import Button from '../../../../components/global/Button';
import {
  getPrintPackagePrintBackgrounds,
  printPackageRequiresConfiguration,
} from '../../../../util/print-packages';
import PrintProductGroupConfiguratorModal from '../../../prints/components/PrintProductGroupConfigurator/modal';
import { getPhotoOrientation } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/PrintProductsGroup/helpers';
import {
  getPrintPackageItemListingLabel,
  getPrintPackageItemPrintProductName,
} from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumPrintPackageOption/helpers';
import Badge from '../../../../components/global/Badge';
import { DIGITAL_DOWNLOADS_MODE } from '../../../../constants/print-packages';
import Alert from '../../../../components/global/Alert';
import {
  getPrintServiceLabels,
  getPrintSizeServiceIds,
} from '../../../../util/print-product-configuration';
import TrashIcon from '../../../../components/icons/TrashIcon';
import { isStandardPrint } from '../../../../util/print-product';
import PrintProductSizeRoesConfiguratorModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/PrintProductsGroup/components/PrintProductSizeRoesConfigurator';
import WarningIcon from '../../../../components/icons/WarningIcon';
import { CUSTOM_PRINT_SUBTYPE } from '../../../../constants/pro-custom-prints';
import CardElementPhotoSelectionControl from '../../../cards/components/CardElementPhoto/CardElementPhotoSelectionControl';
import PrintPackageDigitalDownloadAccountCardModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AlbumPrintPackageOption/components/PrintPackageDigitalDownloadAccountCardModal/PrintPackageDigitalDownloadAccountCardModal';
import {
  isMultiIdentityPackageMembership,
  isMultiPosePackageMembership,
} from '../../../../apps/temp/views/memberships/AlbumMembershipGroup/helpers';
import useIdentityJerseyColors from '../../../../apps/pro/views/HierarchyItem/Matches/hooks/useIdentityJerseyColors';
import PhotoSelectionOverlayModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/DigitalBundleOption/components/PhotoSelectionOverlayModal';

const propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([CART_ITEM.PURCHASED_PRINT_PACKAGE]).isRequired,
    purchasedPrintPackage: PropTypes.shape({
      id: PropTypes.string.isRequired,
      print_package: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        price: PropTypes.shape({
          value: PropTypes.number.isRequired,
          currency: PropTypes.oneOf(Object.values(CURRENCY)),
        }).isRequired,
      }).isRequired,
    }).isRequired,
    photo: PropTypes.shape({
      id: PropTypes.string.isRequired,
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string.isRequired,
          width: PropTypes.number.isRequired,
          height: PropTypes.number.isRequired,
          size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)).isRequired,
        }).isRequired,
      ).isRequired,
    }).isRequired,
    quantity: PropTypes.number.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const PurchasedPrintPackageCartItem = ({
  item,
  onUpdate,
  onRemove,
  watermarkSettings,
}) => {
  const {
    albumPrintPackage,
    purchasedPrintPackage,
    quantity,
    photo,
    albumMembershipGroup,
    configurableItems: itemConfigurableItems,
    isAllPhotos,
    isGreenScreenEnabled,
    identity,
  } = item;

  const { jerseyColors } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId:
      albumMembershipGroup?.album_folder?.id ??
      albumMembershipGroup?.album?.root_album_folder?.id,
  });

  const printPackage = purchasedPrintPackage.print_package;
  const printBackgrounds = isGreenScreenEnabled
    ? getPrintPackagePrintBackgrounds(
        albumPrintPackage,
        albumMembershipGroup,
        jerseyColors,
      )
    : [];
  const allowMultiIdentityPhoto =
    isMultiIdentityPackageMembership(albumMembershipGroup) ?? false;
  const allowMultiPose =
    isMultiPosePackageMembership(albumMembershipGroup) ?? false;

  const [configurableItemsListModalProps, setConfigurableItemsListModalProps] =
    useState(null);

  const [
    digitalDownloadAccountCardModalOpen,
    setDigitalDownloadAccountCardModalOpen,
  ] = useState(false);

  const {
    activeConfigurableItem,
    onItemConfigured,
    onItemsConfigured,
    onConfigurationStart,
    onConfigurationCancel,
    configurableItems,
    onConfigurableItemUpdate,
    activeConfig,
  } = useAlbumPrintPackageCartConfigurator({
    albumPrintPackage: purchasedPrintPackage,
    albumPrintPackageCartItem: item,
    onItemConfiguredComplete: ({
      completeConfigurableItem,
      remainingConfigurableItems,
    }) => {
      if (remainingConfigurableItems.length > 0) {
        setConfigurableItemsListModalProps({
          printPackage,
          completeConfigurableItem,
          remainingConfigurableItems,
        });
      }
    },
    onUpdateCartItem: onUpdate,
    allowMultiPose,
  });

  const remainingItems = configurableItems?.length ?? 0;
  const standardItems = (configurableItems ?? []).filter(configurableItem =>
    isStandardPrint(
      configurableItem?.print_product_configuration?.print_product,
    ),
  );

  const nonConfigurableItems =
    albumPrintPackage?.print_package?.items?.records?.filter(
      printPackageItem =>
        !!printPackageItem.digital_bundle ||
        printPackageItem.grants_purchased_identity,
    );

  return (
    <div
      data-testid="checkout-album-print-package-cart-item"
      className="checkout-cart-item checkout-album-print-package-cart-item"
    >
      <div className="checkout-cart-item-content">
        <div className="checkout-cart-item-figure">
          <CartItemPhotoPreview
            photo={item.photo}
            watermarkSettings={watermarkSettings}
          >
            <span className="checkout-cart-item-photo-icon">
              <img
                src={
                  printPackage?.preview_asset_image?.url ??
                  getAsset('/icon-package-3d.svg')
                }
                alt=""
              />
            </span>
          </CartItemPhotoPreview>
        </div>

        <div className="checkout-cart-item-info">
          <span className="checkout-cart-item-title d-block">
            {printPackage.name}
          </span>

          <Badge className="text-extra-bold" badgeStyle="yellow">
            PAID
          </Badge>

          <div className="checkout-cart-item-info-actions mt-2">
            <CheckoutItemQuantityInput
              onChange={value =>
                onUpdate(item.id, {
                  quantity: value,
                })
              }
              value={quantity}
              removeButtons
            />

            <button
              className="checkout-cart-item-btn-remove"
              onClick={() => onRemove(item.id)}
            >
              <TrashIcon color="#fe5851" size={18} />
            </button>
          </div>
        </div>
      </div>
      {remainingItems > 0 && item && (
        <Button
          btnStyle="link"
          className="purchasing-options-item-btn mt-1"
          onClick={onConfigurationStart}
        >
          <WarningIcon size={16} color="#fe5851" className="mr-1" />
          Configure ({remainingItems}) more {pluralize('item', remainingItems)}
        </Button>
      )}

      {printPackageRequiresConfiguration(printPackage, allowMultiPose) &&
        remainingItems === 0 && (
          <div className="mt-1">
            {(itemConfigurableItems ?? []).map(
              printPackageItemConfiguration => {
                const { printPackageItem } = printPackageItemConfiguration;

                if (!printPackageItem) {
                  return null;
                }

                const printPackageItemName = (() => {
                  if (printPackageItem?.digital_bundle) {
                    return (
                      <>
                        Digital Photo Bundle:{' '}
                        {pluralize(
                          'Photo',
                          printPackageItem?.digital_bundle?.photos_count ?? 1,
                          true,
                        )}
                        <br />
                        (Preferred digital photos are chosen after checkout)
                      </>
                    );
                  }

                  return (
                    printPackageItem?.type?.name ??
                    printPackageItem?.custom_print?.name ??
                    getPrintPackageItemListingLabel(printPackageItem)
                  );
                })();

                const itemServiceLabels = getPrintServiceLabels(
                  printPackageItem.print_product_configuration,
                );

                const itemServiceLabelsStr =
                  itemServiceLabels.length > 0
                    ? ` – Includes ${itemServiceLabels.join(', ')}.`
                    : '';

                return (
                  <div
                    key={printPackageItem.id}
                    className="d-flex align-self-start w-100 mt-1"
                  >
                    <Button
                      key={printPackageItem.id}
                      btnSize="sm"
                      btnStyle="link"
                      onClick={() => {
                        onConfigurableItemUpdate(printPackageItemConfiguration);
                      }}
                      className="text-extra-bold text-nowrap"
                    >
                      {printPackageItem.print_product_configuration
                        ?.print_product?.card?.id
                        ? 'View & Edit'
                        : 'Edit'}
                    </Button>
                    <span className="text-sm text-bold text-dark text-truncate">
                      &nbsp;&ndash;&nbsp;{printPackageItemName}
                      {itemServiceLabelsStr}
                    </span>
                  </div>
                );
              },
            )}
          </div>
        )}
      {nonConfigurableItems?.length > 0 && (
        <div className="mt-1">
          {(nonConfigurableItems ?? []).map(printPackageItem => {
            const printPackageItemName = (() => {
              if (printPackageItem?.grants_purchased_identity) {
                return 'Digital Photo Pass';
              }

              if (printPackageItem?.digital_bundle) {
                return (
                  <>
                    Digital Photo Bundle:{' '}
                    {pluralize(
                      'Photo',
                      printPackageItem?.digital_bundle?.photos_count ?? 1,
                      true,
                    )}
                    <br />
                    (Preferred digital photos are chosen after checkout)
                  </>
                );
              }

              return (
                printPackageItem?.type?.name ??
                printPackageItem?.custom_print?.name ??
                getPrintPackageItemPrintProductName(printPackageItem)
              );
            })();

            const itemServiceLabels = getPrintServiceLabels(
              printPackageItem.print_product_configuration,
            );

            const itemServiceLabelsStr =
              itemServiceLabels.length > 0
                ? ` – Includes ${itemServiceLabels.join(', ')}.`
                : '';

            return (
              <div
                key={printPackageItem.id}
                className="d-flex align-self-start w-100 mt-1"
              >
                <span className="text-sm text-bold text-dark text-truncate">
                  {printPackageItemName}
                  {itemServiceLabelsStr}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {printPackage.digital_downloads_mode ===
        DIGITAL_DOWNLOADS_MODE.INCLUDED && (
        <div className="membership-cart-item-footer">
          <Alert type="info" className="text-sm m-0">
            <span>Includes hi&ndash;res download for the selected photo</span>
          </Alert>
        </div>
      )}
      <AccountCardAlbumCustomPrintConfiguratorModal
        forceOpen={
          !!activeConfigurableItem?.custom_print &&
          activeConfigurableItem?.custom_print?.subtype !==
            CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
        }
        albumCustomPrint={{
          custom_print: activeConfigurableItem?.custom_print,
          logo_image:
            albumMembershipGroup?.album?.logo_image ??
            albumMembershipGroup?.album_folder?.logo_image ??
            albumMembershipGroup?.album?.root_album_folder?.logo_image ??
            albumMembershipGroup?.album_folder?.account?.logo_image ??
            albumMembershipGroup?.album?.creator?.logo_image ??
            albumMembershipGroup?.album?.root_album_folder?.account?.logo_image,
        }}
        onDismiss={onConfigurationCancel}
        onFinished={({ accountCard }) => {
          onItemConfigured({ accountCard });
        }}
        deleteAccountCardOnDismiss={!activeConfig}
        buttonText="Done"
        albumMembershipGroup={albumMembershipGroup}
        defaultAccountCard={activeConfig?.accountCard}
        isAllPhotos={isAllPhotos}
        watermarkSettings={watermarkSettings}
      />
      <CardElementPhotoSelectionControl
        uploadDisabled
        title={`Select ${pluralize(
          'photo',
          activeConfigurableItem?.custom_print?.photos_no,
          true,
        )} for: ${activeConfigurableItem?.custom_print?.name}`}
        photosContext={{
          album_id:
            albumMembershipGroup &&
            (albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          album_folder_id:
            albumMembershipGroup &&
            (!albumMembershipGroup.is_album
              ? albumMembershipGroup.id
              : undefined),
          root_album_folder_id:
            albumMembershipGroup && albumMembershipGroup.is_album
              ? albumMembershipGroup.album.root_album_folder?.id
              : undefined,
          // We rely on a resolver that the membershipGroup would have by
          // default when we don't create a pseudo membership (identities).
          // This allows us to actually discern if this is a membership or not.
          is_album_membership_group: !!albumMembershipGroup?.identities,
          is_all_photos: isAllPhotos,
          identity_id: allowMultiIdentityPhoto ? undefined : identity?.id,
        }}
        forceOpen={
          !!activeConfigurableItem?.custom_print &&
          activeConfigurableItem?.custom_print?.subtype ===
            CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
        }
        onClose={onConfigurationCancel}
        maxPhotosCount={activeConfigurableItem?.custom_print?.photos_no}
        currentSelectedPhotos={
          activeConfig?.photos
            ? activeConfig.photos.map(
                ({ photo: selectedPhoto }) => selectedPhoto,
              )
            : [photo]
        }
        currentSelectedPhoto={
          activeConfigurableItem?.custom_print?.photos_no === 1 &&
          activeConfig?.photos?.length === 1 &&
          allowMultiPose
            ? activeConfig?.photos[0]?.photo
            : undefined
        }
        onChange={photos => {
          onItemConfigured({
            photos: photos?.id
              ? [{ photo: photos, position: 0 }]
              : photos.map((selectedPhoto, index) => ({
                  photo: selectedPhoto,
                  position: index,
                })),
          });
        }}
        watermarkSettings={watermarkSettings}
      />
      <PhotoSelectionOverlayModal
        forceOpen={activeConfigurableItem?.digital_bundle}
        onClose={onConfigurationCancel}
        maxPhotos={activeConfigurableItem?.digital_bundle?.photos_count}
        albumMembershipGroupId={
          isAllPhotos ? undefined : albumMembershipGroup?.id
        }
        albumId={isAllPhotos ? albumMembershipGroup?.id : undefined}
        currentSelectedPhotos={activeConfigurableItem?.photos}
        identityId={allowMultiIdentityPhoto ? undefined : identity?.id}
        onComplete={({ photos }) => onItemConfigured({ photos })}
        watermarkSettings={watermarkSettings}
      />
      {activeConfigurableItem?.print_product_configuration && (
        <>
          {isStandardPrint(
            activeConfigurableItem?.print_product_configuration?.print_product,
          ) && !activeConfig ? (
            <PrintProductGroupConfiguratorModal
              forceOpen
              printProducts={standardItems.map(printProductItem => ({
                id: printProductItem?.id,
                printProductId:
                  printProductItem?.print_product_configuration.print_product
                    .id,
                printProductName:
                  printProductItem?.print_product_configuration.print_product
                    .name,
                printProductSizeId:
                  printProductItem?.print_product_configuration
                    .print_product_size.id,
                printProductSizeName:
                  printProductItem?.print_product_configuration
                    .print_product_size.print_size.name,
                printProductSizeServiceIds: getPrintSizeServiceIds(
                  printProductItem?.print_product_configuration,
                ),
                orientation: getPhotoOrientation(photo),
                // defaultAccountCard: technically supported, but we're not editing
                // on this modal as of now. If we do, we'll need to get each config
                // from the cart Item.
              }))}
              photo={photo}
              onClose={() => {
                onConfigurationCancel();
              }}
              onComplete={configurations => {
                onItemsConfigured(configurations);
              }}
              buttonText="Done"
              albumMembershipGroup={albumMembershipGroup}
              printBackgrounds={printBackgrounds}
              watermarkSettings={watermarkSettings}
              isAllPhotos={isAllPhotos}
              allowMultiPose={printPackage?.is_multi_pose}
              allowMultiIdentity={printPackage?.is_multi_identity}
            />
          ) : (
            <>
              <PrintProductConfiguratorModal
                forceOpen={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product?.card?.id
                }
                printProductId={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product.id
                }
                printProductSizeId={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product_size.id
                }
                printProductSizeServiceIds={getPrintSizeServiceIds(
                  activeConfigurableItem?.print_product_configuration,
                )}
                orientation={getPhotoOrientation(photo)}
                photo={photo}
                onClose={() => {
                  onConfigurationCancel();
                }}
                onComplete={({ accountCard }) => {
                  onItemConfigured({ accountCard });
                }}
                defaultAccountCard={activeConfig?.accountCard}
                buttonText="Done"
                controlsDisabled
                albumMembershipGroup={albumMembershipGroup}
                printBackgrounds={printBackgrounds}
                watermarkSettings={watermarkSettings}
              />
              <PrintProductSizeRoesConfiguratorModal
                isInCart
                forceOpen={
                  !activeConfigurableItem?.print_product_configuration
                    ?.print_product?.card?.id
                }
                printProduct={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product
                }
                printProductSize={
                  activeConfigurableItem?.print_product_configuration
                    ?.print_product_size
                }
                defaultConfiguration={activeConfig?.roesConfiguration}
                photo={photo}
                onComplete={({ roesConfiguration }) => {
                  onItemConfigured({ roesConfiguration });
                }}
                albumMembershipGroup={albumMembershipGroup}
                isAllPhotos={isAllPhotos}
                onClose={() => onConfigurationCancel()}
                watermarkSettings={watermarkSettings}
              />
            </>
          )}
        </>
      )}
      {isGreenScreenEnabled && (
        <PrintPackageDigitalDownloadAccountCardModal
          forceOpen={digitalDownloadAccountCardModalOpen}
          accountCards={(item?.configurableItems ?? [])
            .filter(configItem => !!configItem?.config?.accountCard?.id)
            .map(({ config }) => config.accountCard)}
          onAccountCardSelected={accountCard => {
            onUpdate(item.id, {
              includeDigitalDownload: true,
              digitalDownloadAccountCard: accountCard,
            });
            setDigitalDownloadAccountCardModalOpen(false);
          }}
        />
      )}
      {!!configurableItemsListModalProps && (
        <PrintPackageConfigurableItemCompleteModal
          forceOpen
          onCheckout={() => {
            // Do nothing on checkout
          }}
          onConfigureNextItem={onConfigurationStart}
          {...configurableItemsListModalProps}
          onClose={() => {
            setConfigurableItemsListModalProps(null);
          }}
        />
      )}
    </div>
  );
};

PurchasedPrintPackageCartItem.propTypes = propTypes;

export default PurchasedPrintPackageCartItem;
